import { useEffect, useRef, useState } from 'react'
import { Button, Modal } from '../../../shared/ui'
import { kartenFehler, useCreatePin } from './kartenApi'

interface Props {
  open: boolean
  schoolId: string
  schoolName: string
  onClose: () => void
}

// Die PIN lebt nur im State dieser Komponente und verschwindet beim Schließen.
export default function PinDialog({ open, schoolId, schoolName, onClose }: Props) {
  const create = useCreatePin()
  const [pin, setPin] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  // A request still running when the dialog closes must not drop its PIN into
  // state afterwards (it would show again on reopening) — Astra review.
  const openRef = useRef(open)
  openRef.current = open

  useEffect(() => {
    if (open) return
    setPin(null); setError(null); setCopied(false); create.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  async function erzeugen() {
    try {
      setError(null)
      const r = await create.mutateAsync(schoolId)
      if (openRef.current) setPin(r.pin)
    } catch (e) {
      if (openRef.current) setError(kartenFehler(e))
    }
  }

  async function kopieren() {
    if (!pin) return
    try { await navigator.clipboard.writeText(pin); setCopied(true) } catch { setError('Kopieren nicht möglich — bitte die PIN abschreiben.') }
  }

  return (
    <Modal
      open={open}
      onClose={() => { if (!pin && !create.isPending) onClose() }}
      size="sm"
      title={pin ? 'Neue PIN' : 'Neue PIN erzeugen'}
      subtitle={schoolName}
      footer={
        pin ? (
          <Button variant="primary" onClick={onClose}>Fertig — ich habe die PIN weitergegeben</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={create.isPending}>Abbrechen</Button>
            <Button variant="danger" onClick={erzeugen} loading={create.isPending}>PIN erzeugen</Button>
          </>
        )
      }
    >
      {pin ? (
        <div className="space-y-4 text-center">
          <p className="font-mono text-5xl font-bold tracking-[0.3em] tabular-nums text-on-surface select-all pl-[0.3em]">{pin}</p>
          <Button variant="secondary" icon={copied ? 'check' : 'content_copy'} onClick={kopieren}>
            {copied ? 'Kopiert' : 'PIN kopieren'}
          </Button>
          <p className="text-xs text-on-surface-variant">
            Diese PIN wird nur jetzt angezeigt und kann nicht erneut abgerufen werden.
          </p>
          {error && <p className="text-sm text-rose-600">{error}</p>}
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-on-surface">
            Die alte PIN gilt dann nicht mehr. Alle Karten dieser Schule werden abgemeldet.
          </p>
          {error && <p className="text-sm text-rose-600">{error}</p>}
        </div>
      )}
    </Modal>
  )
}
